/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import {
    AttributeDataType,
    DisplayTemplate,
    format,
    ProductRefinerSource,
    ProductRefinerValue,
    RefinerType
} from '@msdyn365-commerce/retail-proxy';
import { Button, Collapse, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import RangeRefineItem, { RangeRefineItemType } from './range-refine-item';
import RefineItem from './refine-item';
import { IRefineItemCommonProps } from './refine-item.props.common';
import { IRefineItemToggleNotification } from './refine-item-toggle-notification';
import { findMatchingRefinementCriterion, isRangeTypeForInteger } from './utilities';
import RangeRefineItemInput from './range-refine-item-input';
import { IProductRefinerHierarchyExt } from '../../../shared-utils/types';
import { decodeBase64String, isBase64EncodePrefixed } from '../../../shared-utils/base64';

/**
 * Properties associated with the RefineSubmenu component.
 */
export interface IRefineSubmenuProps {
    tempRangeTypeTODO: RangeRefineItemType;
    minValueSliderThumbAriaLabel?: string;
    maxValueSliderThumbAriaLabel?: string;
    productRefinerHierarchy: IProductRefinerHierarchy;
    selectedRefinerValues: ProductRefinerValue[];
    refineItemCommonProps: IRefineItemCommonProps;
    isDisabled: boolean;
    isExpandedOnInitialLoad: boolean;
    shouldHide?: (refiner: IProductRefinerHierarchy, selectedRefinerValues: ProductRefinerValue[]) => boolean;
    context: ICoreContext<IGeneric<IAny>>;
    moduleId: string;
    moduleTypeName: string;
    productCountAriaLabel?: string;
    singleProductCountAriaLabel?: string;
    refineItemAriaLabel?: string;
    refineItemsAriaLabel?: string;
    isMobile?: boolean;
    yesText?: string;
    noText?: string;
    transitRefinerRecordId?: string;
    noResultsFilterValuesText?: string;

    /**
     * The telemetry content
     */
    telemetryContent?: ITelemetryContent;
    onUpdateRefiners(notfication: Readonly<IRefineItemToggleNotification>): void;
    urlBuilder(refiner: IRefineItemToggleNotification): string;
}

/**
 * Refine submenu state.
 */
export interface IRefineSubmenuState extends React.ComponentState {
    expanded: boolean;
    filter: string;
}

/**
 *
 * The RefineSubmenu component renders the submenu category and child items.
 * This computed observes the stateful category hierarchy object.
 * @extends {React.PureComponent<IRefineSubmenuProps>}
 */
class RefineSubmenu extends React.Component<IRefineSubmenuProps, IRefineSubmenuState> {
    private readonly MaxItems = Infinity;

    constructor(props: IRefineSubmenuProps) {
        super(props);

        this._onToggleItem = this._onToggleItem.bind(this);
        this._onToggleSubmenu = this._onToggleSubmenu.bind(this);

        const isExpanded = this.props.isExpandedOnInitialLoad;

        this.state = {
            expanded: isExpanded,
            filter: '',
            isShowFullList: props.productRefinerHierarchy.Values.length <= this.MaxItems
        };
    }

    public shouldComponentUpdate(nextProps: IRefineSubmenuProps, nextState: IRefineSubmenuState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { productRefinerHierarchy, refineItemCommonProps } = this.props;

        if (!productRefinerHierarchy) {
            refineItemCommonProps.telemetry.error('Cannot render submenu without refiner hierarchy data');
        }

        if (productRefinerHierarchy.SourceValue === ProductRefinerSource.Rating && this.props.context.app.config.hideRating) {
            return null;
        }

        if (this.props.shouldHide?.(productRefinerHierarchy, this.props.selectedRefinerValues)) {
            return null;
        }

        let keyName = productRefinerHierarchy.KeyName;

        if (isBase64EncodePrefixed(productRefinerHierarchy)) {
            keyName = keyName?.replace('{base64}:', '');
        }

        const className = classnames(
            'ms-refine-submenu',
            'list-group',
            keyName // Deprecated because of the localization issues. Leaving for backward compatibility reasons.
        );

        return (
            <div className={className} data-recordid={productRefinerHierarchy.RecordId}>
                <Button
                    className={this.state.expanded ? 'ms-refine-sub__toggle_expanded' : 'ms-refine-sub__toggle_collapsed'}
                    aria-label={keyName}
                    onClick={this._onToggleSubmenu}
                    aria-expanded={this.state.expanded}
                >
                    {keyName}
                </Button>
                <Collapse className='ms-refine-submenu__collapse' isOpen={this.state.expanded} timeout={1} onEntered={() => {
                    // Simulate the 'resize' event so that sliders gets rendered correctly.
                    // If not end Slider will always start a position 0 since hidden elements have no width.
                    window.dispatchEvent(new Event('resize'));
                }}>
                    {this._renderChildItems(productRefinerHierarchy)}
                </Collapse>
            </div>
        );
    }

    private _renderChildItems(productRefinerHierarchy: IProductRefinerHierarchyExt): JSX.Element | null {
        if (
            productRefinerHierarchy.DataTypeValue === AttributeDataType.Integer &&
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Range
        ) {
            return this._renderSingleMultiSelect(productRefinerHierarchy);
        }
        // Check if is of integer range type
        if (isRangeTypeForInteger(productRefinerHierarchy.DataTypeValue, productRefinerHierarchy.RangeToPredefinedRanges)) {
            return this._renderRange(productRefinerHierarchy);
        }
        return this._renderSingleMultiSelect(productRefinerHierarchy);
    }

    private _renderSingleMultiSelect(productRefinerHierarchy: IProductRefinerHierarchyExt): JSX.Element | null {
        const {
            isDisabled,
            refineItemCommonProps,
            selectedRefinerValues,
            context,
            productCountAriaLabel,
            singleProductCountAriaLabel,
            refineItemAriaLabel,
            refineItemsAriaLabel,
            isMobile
        } = this.props;
        const isSingleSelect =
            productRefinerHierarchy.RefinerTypeValue === RefinerType.SingleSelect &&
            productRefinerHierarchy.SourceValue === ProductRefinerSource.Rating;
        const role = isSingleSelect ? { role: 'listbox' } : undefined;
        let selectedRefinementCriterion: ProductRefinerValue | undefined = {};
        const refinerValuesList = productRefinerHierarchy.Values || [];
        if (productRefinerHierarchy.DisableSortValues !== true) {
            refinerValuesList.sort(
                (a, b) => {
                    const aValue = a.LeftValueBoundLocalizedString || a.LeftValueBoundString || '';
                    const bValue = b.LeftValueBoundLocalizedString || b.LeftValueBoundString || '';
                    const aDisplayOrder = a.ExtensionProperties?.find(x => x.Key === 'DISPLAYORDER')?.Value?.IntegerValue || 0;
                    const bDisplayOrder = b.ExtensionProperties?.find(x => x.Key === 'DISPLAYORDER')?.Value?.IntegerValue || 0;
                    if (aDisplayOrder !== bDisplayOrder) {
                        return aDisplayOrder - bDisplayOrder;
                    } else {
                        return (aValue).localeCompare(bValue, context.request.locale);
                    }
                }
            );
        }
        const filter = (child: ProductRefinerValue): boolean => {
            if (!this.state.filter) {
                return true;
            }
            const value = isBase64EncodePrefixed(productRefinerHierarchy) && child.LeftValueBoundString
                ? decodeBase64String(child.LeftValueBoundString)
                : child.LeftValueBoundString;
            return !!value?.toLowerCase()?.includes(this.state.filter.toLowerCase());
        };
        const listItems = refinerValuesList.filter(filter).map((child: ProductRefinerValue, index: number) => {
            if (!child) {
                refineItemCommonProps.telemetry.error(
                    `[refine-submenu] Could not render refine item for refiner ${productRefinerHierarchy.RecordId} (${productRefinerHierarchy.KeyName})`
                );
                return null;
            }
            if (index > (this.MaxItems - 1) && !this.state.isShowFullList) {
                return null;
            }

            selectedRefinementCriterion = findMatchingRefinementCriterion(child, selectedRefinerValues, productRefinerHierarchy);

            return (
                <RefineItem
                    parentProductRefinerHierarchy={productRefinerHierarchy}
                    productRefinerValue={child}
                    selectedRefinementCriterion={selectedRefinementCriterion}
                    selectedRefinerValues={selectedRefinerValues.filter(selectedValue =>
                        refinerValuesList.find(value => value.RefinerRecordId === selectedValue.RefinerRecordId)
                    )}
                    refineItemCommonProps={refineItemCommonProps}
                    onToggle={this._onToggleItem}
                    urlBuilder={this.props.urlBuilder}
                    isDisabled={isDisabled}
                    key={index}
                    index={index}
                    context={context}
                    moduleId={this.props.moduleId}
                    moduleTypeName={this.props.moduleTypeName}
                    telemetryContent={this.props.telemetryContent}
                    productCountAriaLabel={productCountAriaLabel}
                    refineItemAriaLabel={refineItemAriaLabel}
                    refineItemsAriaLabel={refineItemsAriaLabel}
                    singleProductCountAriaLabel={singleProductCountAriaLabel}
                    isMobileView={isMobile}
                    yesText={this.props.yesText}
                    noText={this.props.noText}
                    transitRefinerRecordId={this.props.transitRefinerRecordId}
                />
            );
        });

        const keyName = isBase64EncodePrefixed(productRefinerHierarchy)
            ? productRefinerHierarchy.KeyName?.replace('{base64}:', '')
            : productRefinerHierarchy.KeyName;

        return (
            <>
             {refinerValuesList.length > 10 && (
                <div className='ms-refine-submenu__filter__container'>
                    <input
                        className='ms-refine-submenu__filter'
                        type='search'
                        placeholder='Søk...'
                        title='Søk i filter verdier'
                        value={this.state.filter}
                        onChange={(e) => this.setState({ filter: e.target.value })}
                    />
                </div>
             )}
            {listItems.length === 0 && (
                <div className='ms-refine-submenu__filter__no-results'>
                    {format(this.props.noResultsFilterValuesText || 'Ingen resultater for "{0}" funnet.', this.state.filter)}
                </div>
            )}
            <ul
                className={`ms-refine-submenu__list ${keyName}`}
                {...role}
                aria-label={keyName}
            >
                {listItems}
                {productRefinerHierarchy.DataTypeValue === AttributeDataType.Integer &&
                    productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Range && (
                        <RangeRefineItemInput
                            parentProductRefinerHierarchy={productRefinerHierarchy}
                            selectedRefinementCriterion={selectedRefinementCriterion}
                            refineItemCommonProps={refineItemCommonProps}
                            onToggle={this._onToggleItem}
                            isDisabled={isDisabled}
                            rangeType={'input'}
                            key={productRefinerHierarchy.KeyName}
                        />
                    )}
            </ul>
            {refinerValuesList.length > this.MaxItems &&
                <button className={`category-collapse ${this.state.isShowFullList ? 'show':'hide'}`} onClick={()=> this.setState({ isShowFullList: !this.state.isShowFullList })}>
                    {this.state.isShowFullList ? 'Vis færre' : `Vis alt (${refinerValuesList.length - this.MaxItems})`}
                </button>
            }
            </>
        );
    }

    private _getRangeType(productRefinerHierarchy: IProductRefinerHierarchy): 'input' | 'slider' {
        if (
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Range ||
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Slider ||
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.SliderWithBars
        ) {
            return 'slider';
        }
        return 'input';
    }

    private _renderRange(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        const {
            isDisabled,
            refineItemCommonProps,
            selectedRefinerValues,
            context,
            minValueSliderThumbAriaLabel,
            maxValueSliderThumbAriaLabel
        } = this.props;
        const submenuClassNamePrefix = 'ms-refine-submenu__item list-group-item refine-submenu__item';
        const refinerValuesList = productRefinerHierarchy.Values || [];
        const listItems = refinerValuesList.map((child: ProductRefinerValue, index: number) => {
            if (!child) {
                refineItemCommonProps.telemetry.error(
                    `Could not render refine item for refiner ${productRefinerHierarchy.RecordId} (${productRefinerHierarchy.KeyName})`
                );
                return null;
            }

            const selectedRefinementCriterion = findMatchingRefinementCriterion(child, selectedRefinerValues, productRefinerHierarchy);

            const rangeType = this._getRangeType(productRefinerHierarchy);
            const key = selectedRefinementCriterion
                ? `${selectedRefinementCriterion.LeftValueBoundString}-${selectedRefinementCriterion.RightValueBoundString}`
                : `not-selected-${index}`;
            return (
                <li className={`${submenuClassNamePrefix}--range`} key={index}>
                    <RangeRefineItem
                        parentProductRefinerHierarchy={productRefinerHierarchy}
                        productRefinerValue={child}
                        selectedRefinementCriterion={selectedRefinementCriterion}
                        refineItemCommonProps={refineItemCommonProps}
                        onToggle={this._onToggleItem}
                        urlBuilder={this.props.urlBuilder}
                        isDisabled={isDisabled}
                        rangeType={rangeType}
                        key={key}
                        sliderKey={key}
                        context={context}
                        minValueSliderThumbAriaLabel={minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={maxValueSliderThumbAriaLabel}
                        moduleId={this.props.moduleId}
                        moduleTypeName={this.props.moduleTypeName}
                    />
                </li>
            );
        });

        const keyName = isBase64EncodePrefixed(productRefinerHierarchy)
            ? productRefinerHierarchy.KeyName?.replace('{base64}:', '')
            : productRefinerHierarchy.KeyName;

        return <ul className={`ms-refine-submenu__list list-unstyled ${keyName}`}>{listItems}</ul>;
    }

    private _onToggleItem(itemToggleNotification: IRefineItemToggleNotification): void {
        this.props.onUpdateRefiners(itemToggleNotification);
    }

    private _onToggleSubmenu(): void {
        this.setState(prevState => ({
            expanded: !prevState.expanded
        }));
    }
}

export default RefineSubmenu;
